/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import classnames from 'classnames';
import {FilterChecklistOptionBase} from './types';

interface FilterChecklistItemProps<
  T extends FilterChecklistOptionBase = FilterChecklistOptionBase
> {
  classes: Record<string, string>;
  disabled: boolean;
  getOptionLabel: (option: T) => string;
  onToggle: (option: T, checked: boolean) => void;
  option: T;
  optionKey?: string;
  selected: boolean;
}

/**
 * Renders one selectable chip inside a filter checklist.
 *
 * ```typescript
 * import FilterChecklistItem from '@ion/dls/src/filters/FilterChecklistItem';
 *
 * ...
 *
 * <FilterChecklistItem classes={classes}
 *   disabled={disabled}
 *   getOptionLabel={getOptionLabel}
 *   option={option}
 *   optionKey='vehicle'
 *   selected={selected}
 *   onToggle={toggleOption} />
 * ```
 */
export const FilterChecklistItem = <T extends FilterChecklistOptionBase = FilterChecklistOptionBase>({
  classes,
  disabled,
  getOptionLabel,
  onToggle,
  option,
  optionKey,
  selected
}: FilterChecklistItemProps<T>) => {
  const handleClick = useCallback(() => {
    onToggle(option, !selected);
  }, [onToggle, option, selected]);

  return (
    <button type='button'
      data-filter-checklist-key={optionKey}
      disabled={disabled || option.disabled}
      className={classnames(classes.chip, {
        [classes.chipSelected] : selected,
        [classes.chipDisabled] : disabled || option.disabled
      })}
      onClick={handleClick}>
      <span className={classes.chipLabel}>{getOptionLabel(option)}</span>
    </button>
  );
};

export default memo(FilterChecklistItem) as typeof FilterChecklistItem;
